/**
 * Converts raw AI metadata (mock or Gemini) into the normalized result contract
 * used by the Add Content form and `useAiVideoAnalysis`.
 */

/** Fields below this confidence are dropped from auto-fill. */
export const AUTOFILL_CONFIDENCE_THRESHOLD = 0.6;

const conf = (entry) =>
  entry && typeof entry === "object" ? entry.confidence ?? 0 : 0;
const val = (entry) =>
  entry && typeof entry === "object" ? entry.value : entry;
const hasValue = (v) =>
  v !== null &&
  v !== undefined &&
  v !== "" &&
  !(Array.isArray(v) && v.length === 0);

const buildAutofillFields = (raw) => {
  const { basic = {}, intelligence = {} } = raw;

  const candidates = [
    ["name", basic.title, "basic"],
    ["tagline", basic.shortDescription, "basic"],
    ["description", basic.longDescription, "basic"],
    ["genres", basic.genre, "basic"],
    ["language", basic.language, "basic"],
    ["rating", basic.ageRating, "basic"],
    ["year", basic.year, "basic"],
    ["duration", intelligence.runtime, "basic"],
    ["content_warning", basic.contentWarnings, "basic"],
    ["episode_number", intelligence.episodeNumber, "intelligence"],
  ];

  const fields = {};
  for (const [key, entry, group] of candidates) {
    if (!entry) continue;
    const confidence = conf(entry);
    if (confidence < AUTOFILL_CONFIDENCE_THRESHOLD) continue;

    let value = val(entry);
    if (key === "language") value = value?.code ?? value;
    if (key === "content_warning") value = Array.isArray(value) ? value[0] : value;
    if (key === "genres") value = Array.isArray(value) ? value.join(",") : value;

    if (!hasValue(value)) continue;
    fields[key] = { value, confidence, group };
  }
  return fields;
};

const buildTechnicalFields = (raw) => {
  const t = raw.technical || {};
  const keys = [
    "max_quality",
    "frame_rate",
    "aspect_ratio",
    "video_codec",
    "audio_codec",
    "bitrate",
    "hdr",
  ];
  const out = {};
  for (const key of keys) {
    const entry = t[key];
    if (!entry || conf(entry) < AUTOFILL_CONFIDENCE_THRESHOLD) continue;
    const value = val(entry);
    if (hasValue(value)) out[key] = { value, confidence: conf(entry) };
  }
  return out;
};

const buildArtwork = (raw) => {
  const m = raw.media || {};
  const map = {
    portrait: m.poster,
    landscape: m.backdrop,
    logo: m.logo,
    hero: m.heroClip,
  };
  const out = {};
  for (const [slot, entry] of Object.entries(map)) {
    if (!entry || conf(entry) < AUTOFILL_CONFIDENCE_THRESHOLD) continue;
    const value = val(entry);
    if (hasValue(value)) out[slot] = { value, confidence: conf(entry) };
  }
  return out;
};

const buildTrailer = (raw) => {
  const entry = raw.trailer;
  if (!entry || conf(entry) < AUTOFILL_CONFIDENCE_THRESHOLD) return null;
  const value = val(entry);
  return hasValue(value) ? { value, confidence: conf(entry) } : null;
};

const buildAudioTracks = (raw) => {
  const entry = raw.audioTracks;
  if (!entry || conf(entry) < AUTOFILL_CONFIDENCE_THRESHOLD) return [];
  const value = val(entry);
  return Array.isArray(value) ? value : [];
};

/** @param {object} raw  Output of `generateMockMetadata` or `geminiResponseToRawMetadata` */
export const normalizeAnalysisResult = (raw) => ({
  fields: buildAutofillFields(raw),
  technical: buildTechnicalFields(raw),
  artwork: buildArtwork(raw),
  trailer: buildTrailer(raw),
  audioTracks: buildAudioTracks(raw),
  suggestions: {
    basic: raw.basic,
    discovery: raw.discovery,
    media: raw.media,
    intelligence: raw.intelligence,
  },
  model: raw.model,
});

export default normalizeAnalysisResult;
